import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * LoadingSpinner Component
 * Based on 001a-Extension.md specifications
 * 
 * Features:
 * - Full-page or inline loading indicator
 * - Optional progress bar with gradient
 * - Optional message
 * 
 * Variants:
 * - fullscreen: Centered overlay
 * - inline: Inline spinner
 * - progress: With progress bar
 */

interface LoadingSpinnerProps {
  variant?: 'fullscreen' | 'inline' | 'progress'
  message?: string
  progress?: number // 0-100
  className?: string
}

export function LoadingSpinner({ 
  variant = 'inline', 
  message = 'Chargement...', 
  progress,
  className 
}: LoadingSpinnerProps) {
  if (variant === 'fullscreen') {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          {message && (
            <p className="text-sm text-muted-foreground">{message}</p>
          )}
        </div>
      </div>
    )
  }

  if (variant === 'progress') {
    return (
      <div className={cn('flex flex-col items-center gap-4 p-8', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        {message && (
          <p className="text-sm text-muted-foreground">{message}</p>
        )}
        {progress !== undefined && (
          <div className="w-full max-w-xs">
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300 ease-linear"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
            <p className="text-xs text-center text-muted-foreground mt-2">
              {Math.round(progress)}%
            </p>
          </div>
        )}
      </div>
    )
  }

  // inline variant
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Loader2 className="h-4 w-4 animate-spin text-primary" />
      {message && (
        <span className="text-sm text-muted-foreground">{message}</span>
      )}
    </div>
  )
}

/**
 * Usage Examples:
 * 
 * // Fullscreen loading
 * {isLoading && <LoadingSpinner variant="fullscreen" message="Chargement du projet..." />}
 * 
 * // Inline loading
 * <LoadingSpinner variant="inline" message="Sauvegarde..." />
 * 
 * // With progress bar
 * <LoadingSpinner 
 *   variant="progress" 
 *   message="Génération du PDF..." 
 *   progress={uploadProgress}
 * />
 * 
 * // In a card
 * <Card>
 *   {isLoading ? (
 *     <LoadingSpinner variant="inline" message="Chargement des données..." />
 *   ) : (
 *     <CardContent>...</CardContent>
 *   )}
 * </Card>
 * 
 * // Page loading state
 * function ProjectPage() {
 *   const [isLoading, setIsLoading] = useState(true)
 * 
 *   useEffect(() => {
 *     loadProject().finally(() => setIsLoading(false))
 *   }, [])
 * 
 *   if (isLoading) {
 *     return <LoadingSpinner variant="fullscreen" message="Chargement du projet..." />
 *   }
 * 
 *   return <ProjectContent />
 * }
 */
